import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import billingService from '../../api/billingService';

const QuoteForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    quotationNumber: '',
    quotationDate: new Date().toISOString().split('T')[0],
    validUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    customerId: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    gstNumber: '',
    discount: 0,
    status: 'pending',
    paymentStatus: 'not paid',
    items: [],
    notes: '1. This quotation is valid for 7 days.\n2. GST rates are subject to change as per government regulations.\n3. Payment Terms: 100% advance payment.\n4. Delivery Timeline: Within 7 working days after payment confirmation.',
    subTotal: 0,
    totalDiscounts: 0,
    afterDiscounts: 0,
    cgstTotal: 0,
    sgstTotal: 0,
    igstTotal: 0,
    totalAmount: 0
  });

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  useEffect(() => {
    fetchInitialData();
    if (isEdit) fetchQuote();
  }, [id]);

  useEffect(() => {
    if (isEdit) return;

    const searchParams = new URLSearchParams(location.search);
    const preselectedCustomerId = searchParams.get('customerId');
    if (!preselectedCustomerId) return;

    const customer = customers.find(c => c._id === preselectedCustomerId);
    if (customer) {
      handleCustomerChange(preselectedCustomerId, customer);
      const base = `${customer.firstname || ''} ${customer.lastname || ''}`.trim();
      setCustomerSearch(customer.company ? `${base} (${customer.company})` : base);
      setShowCustomerDropdown(false);
      return;
    }

    const fetchCustomerById = async () => {
      try {
        const res = await billingService.getCustomerById(preselectedCustomerId);
        if (res.success && res.data) {
          setCustomers(prev => (
            prev.some(c => c._id === res.data._id) ? prev : [...prev, res.data]
          ));
          handleCustomerChange(preselectedCustomerId, res.data);
          const base = `${res.data.firstname || ''} ${res.data.lastname || ''}`.trim();
          setCustomerSearch(res.data.company ? `${base} (${res.data.company})` : base);
          setShowCustomerDropdown(false);
        }
      } catch (error) {
        console.error('Error fetching preselected customer:', error);
      }
    };

    fetchCustomerById();
  }, [location.search, customers, isEdit]);

  useEffect(() => {
    if (!isEdit || !formData.customerId || customers.length === 0) return;

    const customer = customers.find(c => c._id === formData.customerId);
    if (!customer) return;

    setSelectedCustomer(customer);
    const base = `${customer.firstname || ''} ${customer.lastname || ''}`.trim();
    setCustomerSearch(customer.company ? `${base} (${customer.company})` : base);
  }, [isEdit, formData.customerId, customers]);

  const fetchInitialData = async () => {
    try {
      const [custRes, prodRes] = await Promise.all([
        billingService.getCustomers({ page: 1, limit: 5000 }),
        billingService.getProducts()
      ]);
      if (custRes.success) setCustomers(custRes.data);
      if (prodRes.success) setProducts(prodRes.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const handleCustomerChange = (customerId, selectedCustomerData = null) => {
    const customer = selectedCustomerData || customers.find(c => c._id === customerId);
    setSelectedCustomer(customer);
    setFormData(prev => {
      if (customer) {
        const base = `${customer.firstname || ''} ${customer.lastname || ''}`.trim();
        const searchDisplay = customer.company ? `${base} (${customer.company})` : base;
        setTimeout(() => setCustomerSearch(searchDisplay), 0);
        return {
          ...prev,
          customerId,
          customerName: `${customer.firstname} ${customer.lastname}`.trim(),
          customerEmail: customer.email || '',
          customerPhone: customer.phone || '',
          customerAddress: customer.address || '',
          customerCompany: customer.company || '',
          gstNumber: customer.gstNumber || '',
          paymentStatus: 'not paid'
        };
      }
      setTimeout(() => setCustomerSearch(''), 0);
      return {
        ...prev,
        customerId,
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        customerAddress: '',
        customerCompany: '',
        gstNumber: ''
      };
    });
  };

  const fetchQuote = async () => {
    try {
      const res = await billingService.getQuoteById(id);
      if (res.success) {
        const quote = res.data;
        setFormData({
          ...quote,
          quotationDate: new Date(quote.quotationDate).toISOString().split('T')[0],
          validUntil: new Date(quote.validUntil).toISOString().split('T')[0],
        });
        // Find and set the selected customer
        const customer = customers.find(c => c._id === quote.customerId);
        if (customer) {
          setSelectedCustomer(customer);
          const base = `${customer.firstname || ''} ${customer.lastname || ''}`.trim();
          setCustomerSearch(customer.company ? `${base} (${customer.company})` : base);
        } else {
          setCustomerSearch(quote.customerName || '');
        }
      }
    } catch (error) {
      console.error('Error fetching quote:', error);
    }
  };

  const filteredCustomers = customers.filter((c) => {
    const searchValue = customerSearch.toLowerCase();
    const fullName = `${String(c.firstname || '')} ${String(c.lastname || '')}`.trim().toLowerCase();
    const company = String(c.company || '').toLowerCase();
    const email = String(c.email || '').toLowerCase();
    const phone = String(c.phone || '').toLowerCase();
    const customerId = String(c.customerId || c._id || '').toLowerCase();

    return (
      fullName.includes(searchValue) ||
      company.includes(searchValue) ||
      email.includes(searchValue) ||
      phone.includes(searchValue) ||
      customerId.includes(searchValue)
    );
  });

  const addItem = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          productId: '',
          productName: '',
          description: '',
          units: 'Units',
          quantity: 1,
          tenure: 1,
          costPrice: 0,
          sellingPrice: 0,
          gstRate: 18,
          discount: 0,
          customDescription: '',
          subtotal: 0,
          discountAmount: 0,
          afterDiscount: 0,
          gstAmount: 0,
          total: 0
        }
      ]
    });
  };

  const removeItem = (index) => {
    const newItems = [...formData.items];
    newItems.splice(index, 1);
    calculateTotals(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    const item = { ...newItems[index] };

    if (field === 'productId') {
      const product = products.find(p => p._id === value);
      if (product) {
        item.productId = value;
        item.productName = product.productName;
        item.description = product.description || '';
        item.costPrice = product.costPrice || 0;
        item.sellingPrice = product.sellingPrice || 0;
        item.gstRate = product.gstRate || 18;
        item.units = product.units || 'Units';
      }
    } else {
      item[field] = value;
    }

    // Calculations
    const qty = parseFloat(item.quantity) || 0;
    const tenure = parseFloat(item.tenure) || 1;
    const price = parseFloat(item.sellingPrice) || 0;
    const discAmount = parseFloat(item.discount) || 0;
    
    item.subtotal = qty * price * tenure;
    item.discountAmount = discAmount;
    item.afterDiscount = item.subtotal - item.discountAmount;
    item.gstAmount = (item.afterDiscount * (parseFloat(item.gstRate) || 0)) / 100;
    item.total = item.afterDiscount + item.gstAmount;

    newItems[index] = item;
    calculateTotals(newItems);
  };

  const calculateTotals = (items) => {
    const subTotal = items.reduce((sum, item) => sum + (parseFloat(item.subtotal) || 0), 0);
    const totalDiscounts = items.reduce((sum, item) => sum + (parseFloat(item.discountAmount) || 0), 0);
    const afterDiscounts = subTotal - totalDiscounts;
    const gstTotal = items.reduce((sum, item) => sum + (parseFloat(item.gstAmount) || 0), 0);
    
    setFormData(prev => ({
      ...prev,
      items,
      subTotal,
      totalDiscounts,
      afterDiscounts,
      cgstTotal: gstTotal / 2,
      sgstTotal: gstTotal / 2,
      totalAmount: afterDiscounts + gstTotal
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      alert('Please add at least one item');
      return;
    }

    try {
      setLoading(true);
      const res = isEdit 
        ? await billingService.updateQuote(id, formData)
        : await billingService.createQuote(formData);
      
      if (res.success) {
        navigate(-1);
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
         
          <h4 className="fw-700 mb-0">{isEdit ? 'Edit Quotation' : 'Create New Quotation'}</h4>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row g-4">
          <div className="col-md-9">
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body p-4">
                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <label className="form-label text-muted small fw-600">QUOTE NUMBER</label>
                    <input type="text" className="form-control" value={formData.quotationNumber} placeholder={isEdit ? '' : 'Auto-assigned on save'} disabled />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label text-muted small fw-600">QUOTE DATE</label>
                    <input type="date" className="form-control" value={formData.quotationDate} onChange={e => setFormData({...formData, quotationDate: e.target.value})} required />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label text-muted small fw-600">VALID UNTIL</label>
                    <input type="date" className="form-control" value={formData.validUntil} onChange={e => setFormData({...formData, validUntil: e.target.value})} required />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label text-muted small fw-600">STATUS</label>
                    <select className="form-select" value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}>
                      <option value="pending">Pending</option>
                      <option value="active">Active</option>
                      <option value="expired">Expired</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-md-6 position-relative">
                    <label className="form-label text-muted small fw-600">SELECT CUSTOMER</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0"><i className="bi bi-search"></i></span>
                      <input 
                        type="text" 
                        className="form-control bg-light border-start-0" 
                        placeholder="Search customer by name, email, company or phone..."
                        value={customerSearch}
                        onChange={(e) => {
                          setCustomerSearch(e.target.value);
                          setShowCustomerDropdown(true);
                        }}
                        onFocus={() => setShowCustomerDropdown(true)}
                        required={!formData.customerId}
                      />
                    </div>
                    {showCustomerDropdown && (
                      <div className="dropdown-menu show w-100 shadow-sm border-0 mt-1 overflow-auto" style={{ maxHeight: '300px', zIndex: 1000 }}>
                        {filteredCustomers.length > 0 ? (
                          filteredCustomers.map(c => (
                            <button 
                              key={c._id} 
                              type="button" 
                              className="dropdown-item py-2" 
                              onClick={() => {
                                handleCustomerChange(c._id);
                                const baseName = `${c.firstname || ''} ${c.lastname || ''}`.trim();
                                setCustomerSearch(c.company ? `${baseName} (${c.company})` : baseName);
                                setShowCustomerDropdown(false);
                              }}
                            >
                              <div className="fw-600">{`${c.firstname || ''} ${c.lastname || ''}`.trim() || c.customerName || 'Unnamed Customer'}</div>
                              <div className="text-xs text-muted">{c.company || 'Individual'} • {c.email || c.phone || c.customerId || c._id}</div>
                            </button>
                          ))
                        ) : (
                          <div className="dropdown-item disabled text-muted">No customers found</div>
                        )}
                      </div>
                    )}
                  </div>
                  {selectedCustomer && (
                    <div className="col-md-6">
                      <div className="p-3 bg-light rounded-3">
                        <h6 className="fw-700 mb-2">Customer Details</h6>
                        <div className="row text-xs">
                          <div className="col-6 mb-1"><strong>Email:</strong> {selectedCustomer.email}</div>
                          <div className="col-6 mb-1"><strong>Phone:</strong> {selectedCustomer.phone}</div>
                          <div className="col-6 mb-1"><strong>GST:</strong> {selectedCustomer.gstNumber || 'N/A'}</div>
                          <div className="col-12"><strong>Address:</strong> {selectedCustomer.address || 'N/A'}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="table-responsive">
                  <table className="table table-borderless align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th style={{width: '25%'}}>Product & Description</th>
                        <th style={{width: '10%'}}>Tenure</th>
                        <th style={{width: '10%'}}>Qty</th>
                        <th style={{width: '15%'}}>Price</th>
                        <th style={{width: '10%'}}>Disc (Amt)</th>
                        <th style={{width: '10%'}}>GST %</th>
                        <th style={{width: '15%'}}>Total</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.items.map((item, index) => (
                        <tr key={index} className="border-bottom">
                          <td className="py-3">
                            <select className="form-select form-select-sm mb-1" value={item.productId} onChange={e => handleItemChange(index, 'productId', e.target.value)} required>
                              <option value="">Select product...</option>
                              {products.map(p => <option key={p._id} value={p._id}>{p.productName}</option>)}
                            </select>
                            <textarea 
                              className="form-control form-control-sm text-xs" 
                              rows="1" 
                              placeholder="Custom description..."
                              value={item.description}
                              onChange={e => handleItemChange(index, 'description', e.target.value)}
                            ></textarea>
                          </td>
                          <td>
                            <input type="number" className="form-control form-control-sm" value={item.tenure} onChange={e => handleItemChange(index, 'tenure', e.target.value)} required min="1" />
                          </td>
                          <td>
                            <input type="number" className="form-control form-control-sm" value={item.quantity} onChange={e => handleItemChange(index, 'quantity', e.target.value)} required min="1" />
                          </td>
                          <td>
                            <input type="number" className="form-control form-control-sm" value={item.sellingPrice} onChange={e => handleItemChange(index, 'sellingPrice', e.target.value)} required />
                          </td>
                          <td>
                            <input type="number" className="form-control form-control-sm" value={item.discount} onChange={e => handleItemChange(index, 'discount', e.target.value)} />
                          </td>
                          <td>
                            <input type="number" className="form-control form-control-sm" value={item.gstRate} onChange={e => handleItemChange(index, 'gstRate', e.target.value)} />
                          </td>
                          <td className="fw-600">₹{(item.total || 0).toFixed(2)}</td>
                          <td>
                            <button type="button" className="btn btn-sm btn-light text-danger" onClick={() => removeItem(index)}><i className="bi bi-trash"></i></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={addItem}>
                  <i className="bi bi-plus-lg me-1"></i> Add Item
                </button>
              </div>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-body p-4">
                <label className="form-label text-muted small fw-600">NOTES & TERMS</label>
                <textarea className="form-control" rows="6" value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})}></textarea>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-0 shadow-sm sticky-top" style={{top: '20px'}}>
              <div className="card-header bg-transparent border-0 pt-4 px-4">
                <h6 className="fw-700 mb-0">Summary</h6>
              </div>
              <div className="card-body p-4">
                <div className="d-flex justify-content-between mb-2 small">
                  <span className="text-muted">Sub Total:</span>
                  <span className="fw-600">₹{formData.subTotal.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2 small">
                  <span className="text-muted">Discounts:</span>
                  <span className="fw-600 text-danger">-₹{formData.totalDiscounts.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2 small">
                  <span className="text-muted">CGST ({(formData.items[0]?.gstRate/2) || 9}%):</span>
                  <span className="fw-600">₹{formData.cgstTotal.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between mb-4 small">
                  <span className="text-muted">SGST ({(formData.items[0]?.gstRate/2) || 9}%):</span>
                  <span className="fw-600">₹{formData.sgstTotal.toFixed(2)}</span>
                </div>
                <hr />
                <div className="d-flex justify-content-between mb-4">
                  <span className="fw-700">Grand Total:</span>
                  <span className="fw-700 text-primary h5 mb-0">₹{formData.totalAmount.toFixed(2)}</span>
                </div>
                <button type="submit" className="btn btn-primary w-100 py-2 mb-2" disabled={loading}>
                  {loading ? 'Saving...' : (isEdit ? 'Update Quotation' : 'Save Quotation')}
                </button>
                <button type="button" className="btn btn-light w-100 py-2" onClick={() => navigate(-1)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default QuoteForm;
